import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["type", "disabled"]
const _hoisted_2 = {
  key: 2,
  class: "inline-flex items-center"
}

import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

export interface Props {
  text?: string
  loadingText?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'outline' | 'forest'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  icon?: any
  iconPosition?: 'left' | 'right'
  iconOnly?: boolean
  fullWidth?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseButton',
  props: {
    text: { default: '' },
    loadingText: { default: 'Loading...' },
    type: { default: 'button' },
    variant: { default: 'primary' },
    size: { default: 'md' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    icon: { default: undefined },
    iconPosition: { default: 'left' },
    iconOnly: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false }
  },
  emits: ["click"],
  setup(__props: any) {

const LoaderIcon = Loader2

const props = __props



// Computed classes
const buttonClasses = computed(() => {
  const baseClasses = [
    'inline-flex',
    'justify-center',
    'items-center',
    'font-bold',
    'rounded-[2rem]',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'transform',
    'hover:scale-[1.02]',
    'active:scale-[0.98]',
  ]

  // Size classes
  const sizeClasses = {
    xs: ['px-2.5', 'py-1', 'text-xs'],
    sm: ['px-4', 'py-1.5', 'text-xs md:text-sm'],
    md: ['px-6', 'py-2.5', 'text-sm md:text-base'],
    lg: ['px-8', 'py-3.5', 'text-base md:text-lg'],
    xl: ['px-10', 'py-4', 'text-lg md:text-xl'],
  }

  // Variant classes
  const variantClasses = {
    primary: [
      'text-[#1a1a1a]',
      'bg-primary',
      'hover:bg-primary-hover',
      'active:bg-primary-active',
      'focus:ring-primary/50',
      'shadow-md',
      'hover:shadow-lg',
      'border',
      'border-amber-400/50',
    ],
    secondary: [
      'text-white',
      'bg-secondary',
      'hover:bg-secondary-hover',
      'active:bg-secondary-active',
      'focus:ring-secondary/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    danger: [
      'text-white',
      'bg-danger',
      'hover:bg-danger-hover',
      'active:bg-danger-active',
      'focus:ring-danger/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    success: [
      'text-white',
      'bg-success',
      'hover:bg-success-hover',
      'active:bg-success-active',
      'focus:ring-success/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    warning: [
      'text-white',
      'bg-warning',
      'hover:bg-warning-hover',
      'active:bg-warning-active',
      'focus:ring-warning/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
    ghost: [
      'text-gray-700',
      'dark:text-gray-200',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500',
    ],
    outline: [
      'text-secondary',
      'dark:text-secondary-dark-text',
      'border-2',
      'border-secondary/60',
      'dark:border-secondary-dark/60',
      'hover:bg-secondary-bg',
      'dark:hover:bg-secondary-dark-bg/30',
      'focus:ring-secondary/50',
    ],
    forest: [
      'text-white',
      'bg-[#0b6623]',
      'hover:bg-[#08501b]',
      'active:bg-[#063d14]',
      'focus:ring-[#0b6623]/50',
      'shadow-md',
      'hover:shadow-lg',
    ],
  }

  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []

  // Icon-only classes
  const iconOnlyClasses = props.iconOnly ? ['aspect-square', 'p-2.5'] : []

  return [
    ...baseClasses,
    ...(sizeClasses[props.size] || sizeClasses.md),
    ...(variantClasses[props.variant] || variantClasses.primary),
    ...widthClasses,
    ...iconOnlyClasses,
  ]
})

const iconSize = computed(() => {
  const sizes: Record<string, string> = {
    xs: 'h-3.5 w-3.5',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6',
  }
  return sizes[props.size] || 'h-5 w-5'
})

const iconColorClass = computed(() => {
  if (props.variant === 'primary') {
    return 'text-[#1a1a1a]'
  }
  if (props.variant === 'secondary' || props.variant === 'forest') {
    return 'text-white'
  }
  if (props.variant === 'outline') {
    return 'text-secondary dark:text-secondary-dark-text'
  }
  if (props.variant === 'ghost') {
    return 'text-gray-500 dark:text-gray-400'
  }
  return 'text-current'
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("button", {
    type: _ctx.type,
    disabled: _ctx.disabled || _ctx.loading,
    class: _normalizeClass(buttonClasses.value),
    onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('click')))
  }, [
    (_ctx.loading)
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(LoaderIcon)), {
          key: 0,
          class: "animate-spin -ml-1 mr-2.5 h-5 w-5 text-current"
        }))
      : (_ctx.icon && _ctx.iconPosition === 'left')
        ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.icon), {
            key: 1,
            class: _normalizeClass([iconSize.value, iconColorClass.value]),
            style: _normalizeStyle(_ctx.iconOnly ? {} : { marginRight: '0.5rem' })
          }, null, 8, ["class", "style"]))
        : _createCommentVNode("", true),
    (!_ctx.iconOnly)
      ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
          _renderSlot(_ctx.$slots, "default", {}, () => [
            _createTextVNode(_toDisplayString(_ctx.loading ? _ctx.loadingText : _ctx.text), 1)
          ])
        ]))
      : _createCommentVNode("", true),
    (!_ctx.loading && _ctx.icon && _ctx.iconPosition === 'right')
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.icon), {
          key: 3,
          class: _normalizeClass([iconSize.value, iconColorClass.value]),
          style: _normalizeStyle(_ctx.iconOnly ? {} : { marginLeft: '0.5rem' })
        }, null, 8, ["class", "style"]))
      : _createCommentVNode("", true)
  ], 10, _hoisted_1))
}
}

})